import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { Bookmark, Folder, WorkEngagementBookmarksArray } from '../types';
import { WorkEngagementBookmarksArraySchema } from '../schemas/user-bookmark-schemas';
import { BASE_DASHBOARD_URL } from '@/requests/constants';
import { fetchApi } from '@/requests/api';
import { useSession } from '@/providers/session-provider';
import { WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY } from './use-get-user-bookmarks';

export function useGetWorkEngagementBookmarks<
  TData = WorkEngagementBookmarksArray,
>(
  workEngagementIds?: string[],
  options?: Omit<
    UseQueryOptions<WorkEngagementBookmarksArray, Error, TData>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TData, Error> {
  const {
    data: { session },
  } = useSession();

  return useQuery({
    ...options,
    queryKey: [WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY, workEngagementIds],
    queryFn: async ({ signal }) => {
      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement?work_engagement_ids=${workEngagementIds?.join(',')}`,
        signal,
      });

      if (!res.ok) {
        throw new Error(
          `Failed to fetch work engagement bookmarks: ${res.status}`
        );
      }

      const data = WorkEngagementBookmarksArraySchema.safeParse(
        await res.json()
      );
      if (!data.success) {
        console.error('Invalid work engagement bookmarks data', data.error);
        throw new Error('Invalid work engagement bookmarks data');
      }
      return data.data;
    },
    enabled: !!session.token && !!workEngagementIds?.length,
    staleTime: 60 * 5 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useGetWorkEngagementBookmarksBookmarks(
  workEngagementIds: string[],
  options?: Omit<
    UseQueryOptions<WorkEngagementBookmarksArray, Error, Bookmark[]>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<Bookmark[], Error> {
  return useGetWorkEngagementBookmarks(workEngagementIds, {
    ...options,
    select: (data) => data.flatMap((item) => item.bookmarks),
  });
}

export function useGetWorkEngagementFolders(
  workEngagementIds: string[],
  options?: Omit<
    UseQueryOptions<WorkEngagementBookmarksArray, Error, Folder[]>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<Folder[], Error> {
  return useGetWorkEngagementBookmarks(workEngagementIds, {
    ...options,
    select: (data) => data.flatMap((item) => item.folders),
  });
}
